import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './Styles/VideoPlayer.css';
import '../constants/styleConstants.css';
import axiosClient from '../services/axios_client';
import { LOCAL_STORAGE_KEYS } from '../constants/Constants';
import LoadingSpinner from '../Components/LoadingSpinner';
import logo from '../assets/logo.png'; // Import the logo

// --- Type Definitions ---
type VideoData = {
  id: string;
  title: string;
  description?: string;
  category: { name: string } | null;
  likes: number;
  comments: number;
  is_liked: boolean;
};

type Comment = {
  id: string | number;
  user: { name: string };
  content: string;
  created_at: string;
};

type PagedComments = {
  data: Comment[];
  current_page: number;
  last_page: number;
};

// --- Helper Function ---
const formatTimeAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

// --- Main Component ---
const VideoPlayer: React.FC = () => {
  const { id: videoId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const observer = useRef<IntersectionObserver | null>(null);

  // --- State Management ---
  const [video, setVideo] = useState<VideoData | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentPage, setCommentPage] = useState(1);
  const [hasMoreComments, setHasMoreComments] = useState(true);
  const [newComment, setNewComment] = useState('');

  // Status States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // --- Authentication & Data Fetching ---
  useEffect(() => {
    const isLoggedIn = localStorage.getItem(LOCAL_STORAGE_KEYS.isLoggedIn) === 'true';
    if (!isLoggedIn) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.intendedVideoPath, `/video/${videoId}`);
      navigate('/login');
      return;
    }

    if (!videoId) {
      setError("Video ID is missing.");
      setLoading(false);
      return;
    }

    const loadVideoDetails = async () => {
      try {
        const { data } = await axiosClient.get<VideoData>(`/videos/${videoId}`);
        setVideo(data);
      } catch (e) {
        setError("Could not load video. It may be private or has been removed.");
      } finally {
        setLoading(false);
      }
    };

    loadVideoDetails();
  }, [videoId, navigate]);

  // --- Comments Logic ---
  const fetchComments = useCallback(async (page: number) => {
    if (!videoId) return;
    setCommentsLoading(true);
    try {
      const { data } = await axiosClient.get<PagedComments>(`/videos/${videoId}/comments?page=${page}`);
      setComments(prev => (page === 1 ? data.data : [...prev, ...data.data]));
      setCommentPage(data.current_page + 1);
      setHasMoreComments(data.current_page < data.last_page);
    } catch (e) {
      setError("Could not load comments.");
    } finally {
      setCommentsLoading(false);
    }
  }, [videoId]);

  useEffect(() => {
    fetchComments(1); // Initial comment fetch
  }, [fetchComments]);

  const lastCommentRef = useCallback((node: HTMLLIElement | null) => {
    if (commentsLoading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMoreComments) {
        fetchComments(commentPage);
      }
    });
    if (node) observer.current.observe(node);
  }, [commentsLoading, hasMoreComments, commentPage, fetchComments]);

  // --- User Actions ---
  const handleLike = async () => {
    if (!video) return;
    setVideo(v => v ? { ...v, is_liked: !v.is_liked, likes: v.is_liked ? v.likes - 1 : v.likes + 1 } : null);
    try {
      await axiosClient.post(`/videos/${video.id}/like`);
    } catch (e) { // Revert on error
      setVideo(v => v ? { ...v, is_liked: !v.is_liked, likes: v.is_liked ? v.likes - 1 : v.likes + 1 } : null);
    }
  };

  const submitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!video || !newComment.trim()) return;
    setIsSubmitting(true);
    try {
      const { data: savedComment } = await axiosClient.post<Comment>(`/videos/${video.id}/comments`, { content: newComment.trim() });
      setComments(prev => [savedComment, ...prev]);
      setVideo(v => v ? { ...v, comments: v.comments + 1 } : null);
      setNewComment('');
    } catch (e) {
      alert("Failed to post comment.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- Render Logic ---
  if (loading) return <div className="loading-container"><LoadingSpinner /></div>;
  if (error) return <div className="panel error" role="alert">{error}</div>;
  if (!video) return <div className="panel">Video not found.</div>;

  const videoStreamSrc = `${axiosClient.defaults.baseURL}videos/${video.id}/stream`;

  return (
    <div className="video-page">
      <header className="topbar">
        <button className="btn back-btn" onClick={() => navigate(-1)} aria-label="Go back">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
        </button>
        <div className="brand" onClick={() => navigate('/')} role="button" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={logo} alt="Hameruha Logo" className="logo" />
          <span className="company-name" style={{ fontWeight: 600, fontSize: 20 }}>Hameruha</span>
        </div>
        {/* This empty div acts as a spacer to keep the logo centered */}
        <div style={{ width: 44, flexShrink: 0 }} />
      </header>

      <main className="player-content">
        <h1 style={{ fontSize: '1.75rem', fontWeight: '600', color: '#101828', marginBottom: '1rem' }}>
          {video.title}
        </h1>
        <div className="player-shell">
          <div className="player-aspect">
            <video key={video.id} src={videoStreamSrc} controls autoPlay playsInline style={{ width: '100%', height: '100%' }} />
          </div>
        </div>

        <section className="video-header">
          {/* The title has been moved above the player */}
          <div className="video-actions">
            <button className={`btn ${video.is_liked ? 'primary' : ''}`} onClick={handleLike}>
              <svg viewBox="0 0 24 24" width="18" height="18"><path fill="currentColor" d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 6 4 4 6.5 4c1.74 0 3.41 1 4.22 2.44C11.09 5 12.76 4 14.5 4 17 4 19 6 19 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" /></svg>
              {video.likes}
            </button>
          </div>
        </section>

        {video.description && <section className="description-panel"><p>{video.description}</p></section>}

        <section className="comments">
          <h2 className="comments-title">Comments ({video.comments})</h2>
          <form className="comment-form" onSubmit={submitComment}>
            <input value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Add a comment..." disabled={isSubmitting} />
            <button type="submit" className="btn primary" disabled={isSubmitting || !newComment.trim()}>{isSubmitting ? 'Posting...' : 'Post'}</button>
          </form>

          <ul className="comment-list">
            {comments.map((c, index) => (
              <li key={c.id} className="comment-item" ref={comments.length === index + 1 ? lastCommentRef : null}>
                <div className="avatar" aria-hidden="true">👤</div>
                <div className="comment-body">
                  <div className="comment-meta">
                    <span className="author">{c.user.name}</span>
                    <span className="time">{formatTimeAgo(c.created_at)}</span>
                  </div>
                  <p className="comment-text">{c.content}</p>
                </div>
              </li>
            ))}
            {commentsLoading && <div className="loading-container small"><LoadingSpinner /></div>}
            {!commentsLoading && comments.length === 0 && <li className="comment-empty">No comments yet. Be the first!</li>}
          </ul>
        </section>
      </main>
    </div>
  );
};

export default VideoPlayer;